import type { FormEvent, MouseEvent } from 'react'

import { CreatorPermission, type MeInformation, TeamMemberRole } from '@api'
import type { ClassValue } from 'clsx'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

import { CreatorFeatures } from '@/modules/accounts/types'
import type { Creator } from '@/modules/creators/types'

/**
 * Combines and merges multiple class names or class name arrays into a single string of class names.
 * This function uses the `clsx` library to concatenate and merge class names,
 * and the `tailwind-merge` library to handle Tailwind CSS class merging.
 * @param inputs The class names or class name arrays to combine and merge.
 * @returns A string of merged class names.
 */
export const classNames = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs))
}

/**
 * @function dateToString
 * @description converts js Date object to string
 * @param {Date} date
 * @returns {string} date, converted to string, format yyyy-mm-dd
 */
export const dateToString = (date: Date): string => {
  date = new Date(date.getTime())
  //date.setUTCHours(0, 0, 0, 0);

  return (
    date.getFullYear() +
    '-' +
    (date.getMonth() + 1).toString().padStart(2, '0') +
    '-' +
    date.getDate().toString().padStart(2, '0')
  ) //date.toISOString().split("T")[0];
}

// Returns a number to work for sheet functions, like SUM
export const formatNumbersForSheet = (value: string | number): number => {
  const result = String(value).replace(/[^0-9./-]/g, '')

  return isNaN(parseFloat(result)) ? 0 : +parseFloat(result).toFixed(2)
}

/**
 * Transforms hex-Code to rgba with opacity
 * @param hexCode Hex-Code
 * @param opacity Opacity
 * @returns rgba
 */
export const convertHexToRGBA = (hexCode: string, opacity = 1) => {
  let hex = hexCode.replace('#', '')

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
  }

  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  /* Backward compatibility for whole number based opacity values. */
  if (opacity > 1 && opacity <= 100) {
    opacity = opacity / 100
  }

  return `rgba(${r},${g},${b},${opacity})`
}

/**
 * @function getPercentValue
 * @description Formats a value to a percent number
 * @returns {String} formatted value
 * i.e. 0.56 => 56%
 */
export const getPercentValue = (value: number, digits = 0): string => {
  if (isNaN(value)) {
    return '0%'
  }

  return `${(value * 100).toFixed(digits)}%`
}

/**
 * Converts a number to a string with commas as thousand separators in American style.
 *
 * @param num The number to be formatted.
 * @returns The formatted number as a string in American style.
 */
export function formatNumberWithCommas(num = 0, digits = 0) {
  const numStr = num?.toFixed(digits)
  const formattedNum = numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  return formattedNum
}

// Todo: Refactor xD
export function formatNumberWithPoints(num = 0) {
  const numStr = num.toString()
  const formattedNum = numStr.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

  return formattedNum
}

export const formatNumberToCompact = Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1,
}).format

/**
 * @function debounce
 * @description prevents extra activations or slow functions from triggering too often
 * @param {function} func function, to debounce
 * @param {number} delay time in milliseconds
 */
export const debounce = <F extends (...args: Parameters<F>) => ReturnType<F>>(
  func: F,
  delay = 1000,
): ((...args: Parameters<F>) => void) => {
  let timerId: number | null

  return (...args: Parameters<F>) => {
    if (timerId) {
      clearTimeout(timerId)
    }

    timerId = window.setTimeout(() => {
      func(...args)
      timerId = null
    }, delay)
  }
}

/**
 * Formats time hours
 * @param hours
 * @returns formatted hours with AM / PM
 */
export const formatAMPM = (hours: number) => {
  const ampm = hours >= 12 ? 'pm' : 'am'

  hours = hours % 12
  hours = hours ? hours : 12 // the hour '0' should be '12'

  return hours + ampm
}

export function formatSeconds(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = Math.floor(seconds % 60)

  let result = ''

  if (hours > 0) {
    result += `${hours}h `
  }

  if (minutes > 0 || hours > 0) {
    result += `${minutes}m `
  }

  if (remainingSeconds > 0 || (hours === 0 && minutes === 0)) {
    result += `${remainingSeconds}s`
  }

  return result.trim()
}

export function unEscape(htmlStr: string) {
  htmlStr = htmlStr.replace(/&lt;/g, '<')
  htmlStr = htmlStr.replace(/&gt;/g, '>')
  htmlStr = htmlStr.replace(/&quot;/g, '"')
  htmlStr = htmlStr.replace(/&#39;/g, "'")
  htmlStr = htmlStr.replace(/&amp;/g, '&')

  return htmlStr
}

export const canSeeOverview = (
  account: MeInformation | null,
  creators: Creator[],
  isCreatorProcessCompleted: boolean,
) => {
  return (
    hasPermissionForAnyCreator(
      account,
      creators,
      CreatorFeatures.stats,
      CreatorPermission.SEE_STATS,
      isCreatorProcessCompleted,
    ) ||
    hasPermissionForAnyCreator(
      account,
      creators,
      CreatorFeatures.tracking,
      CreatorPermission.SEE_CHATTER_TRACKING,
      isCreatorProcessCompleted,
    )
  )
}

export const hasPermissionForAnyCreator = (
  account: MeInformation | null,
  creators: Creator[],
  feature: CreatorFeatures,
  permission: CreatorPermission,
  isCreatorProcessCompleted: boolean,
  includeRoleCheck = true,
) => {
  return (
    !isCreatorProcessCompleted ||
    (includeRoleCheck &&
      [TeamMemberRole.LEADER, TeamMemberRole.ADMIN].some(
        (role) => account?.memberRole === role,
      )) ||
    creators.some((creator) => hasPermission(creator, feature, permission))
  )
}

export function hasPermission(
  creator: Creator,
  feature?: CreatorFeatures,
  permission?: CreatorPermission,
) {
  if (!feature && !permission) return true

  const hasFeature = feature ? creator.features.includes(feature) : true
  const hasPermission = permission
    ? creator.permissions.includes(permission)
    : true

  return hasFeature && hasPermission
}

export function formatMinutesAndHours(totalMinutes: number) {
  const hours = Math.trunc(totalMinutes / 60)
  const minutes = Math.trunc(totalMinutes % 60)

  return [
    hours ? `${hours} hr${hours === 1 ? '' : 's'}` : '',
    minutes ? `${minutes} min${minutes === 1 ? '' : 's'}` : '',
  ]
    .filter(Boolean)
    .join(' ')
}

export function formatSecondsAndMinutes(totalSeconds: number) {
  const minutes = Math.trunc(totalSeconds / 60)
  const seconds = Math.trunc(totalSeconds % 60)

  return [
    minutes ? `${minutes} min${minutes === 1 ? '' : 's'}` : '',
    seconds ? `${seconds} sec${seconds === 1 ? '' : 's'}` : '',
  ]
    .filter(Boolean)
    .join(' ')
}

export function toPlural(count: number, single: string) {
  return `${single}${count === 1 ? '' : 's'}`
}

export function stopEventPropagation(event: MouseEvent<HTMLElement>) {
  event.stopPropagation()
}

export function preventDefaultEvent(
  event: MouseEvent<HTMLElement> | FormEvent<HTMLFormElement>,
) {
  event.preventDefault()
}
